import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Zap, AlertCircle, Users, Settings, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { formatPair } from '../../utils/commodities';

import { fmtNumber } from '../../utils/format';
const TradingControl = () => {
  const [binaryTrades, setBinaryTrades] = useState([]);
  const [arbitrageInvestments, setArbitrageInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [tradeSettings, setTradeSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState({ trade_mode: 'normal', win_rate: 50, apply_to: 'all', reason: '' });
  const [settingsLoading, setSettingsLoading] = useState(false);

  useEffect(() => {
    fetchActiveTrades();
    fetchUsers();
  }, []);

  const getToken = () => localStorage.getItem('adminToken');

  const fetchActiveTrades = async () => {
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [binary, arbitrage] = await Promise.all([
        api.get('/admin/binary-trades?status=active', { headers }).catch(() => ({ data: { trades: [] } })),
        api.get('/admin/arbitrage-investments?status=active', { headers }).catch(() => ({ data: [] })),
      ]);
      setBinaryTrades(binary.data?.trades || binary.data || []);
      setArbitrageInvestments(arbitrage.data || []);
    } catch {
      toast.error('Failed to fetch active trades');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/admin/users?limit=500', { headers: { Authorization: `Bearer ${getToken()}` } });
      setUsers(res.data?.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const handleBinaryOutcome = async (tradeId, outcome) => {
    try {
      await api.post('/admin/binary-trades/control-outcome',
        { trade_id: tradeId, outcome },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(`Trade outcome set to ${outcome}`);
      fetchActiveTrades();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set trade outcome');
    }
  };

  const handleArbitrageOutcome = async (investmentId, outcome) => {
    try {
      await api.post('/admin/arbitrage-investments/control-outcome',
        { investment_id: investmentId, outcome, profit_multiplier: 1.0 },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(`Investment outcome set to ${outcome}`);
      fetchActiveTrades();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set investment outcome');
    }
  };

  const loadUserTradeSettings = async (userId) => {
    setSelectedUser(userId);
    setSettingsLoading(true);
    try {
      const res = await api.get(`/admin/user/${userId}/trade-settings`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const ts = res.data?.trade_settings || {};
      setTradeSettings(ts);
      setSettingsForm({
        trade_mode: ts.trade_mode || 'normal',
        win_rate: ts.win_rate || 50,
        apply_to: ts.apply_to || 'all',
        reason: '',
      });
    } catch {
      setTradeSettings(null);
      setSettingsForm({ trade_mode: 'normal', win_rate: 50, apply_to: 'all', reason: '' });
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveUserTradeSettings = async () => {
    if (!selectedUser) return;
    try {
      setSettingsLoading(true);
      await api.put(`/admin/user/${selectedUser}/trade-settings`, settingsForm, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success('Trade settings saved');
      loadUserTradeSettings(selectedUser);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const selectedUserInfo = users.find(u => (u.id || u.user_id) === selectedUser);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="trading-control-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Trading Control</h1>
          <p className="text-gray-400 text-sm">Control individual trade outcomes and per-user trade modes</p>
        </div>
        <button onClick={() => { setLoading(true); fetchActiveTrades(); }} className="p-2 bg-gray-800 rounded-lg text-gray-400 hover:text-white">
          <RefreshCw className="h-5 w-5" />
        </button>
      </div>

      {/* Per-User Trade Settings */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6" data-testid="user-trade-settings">
        <h2 className="text-lg font-bold text-white flex items-center mb-4">
          <Settings className="h-5 w-5 mr-2 text-purple-400" />
          Per-User Trade Mode
        </h2>
        <p className="text-gray-400 text-sm mb-4">
          Set trade outcome mode for specific users. This affects ALL their future trades automatically.
        </p>

        {/* User Selector */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-gray-400 text-sm mb-1 block">Select User</label>
            <select
              value={selectedUser || ''}
              onChange={(e) => e.target.value && loadUserTradeSettings(e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              data-testid="user-selector"
            >
              <option value="">Choose a user...</option>
              {users.map(u => (
                <option key={u.id || u.user_id} value={u.id || u.user_id}>
                  {u.email} — ${(u.balance || 0).toLocaleString()}
                </option>
              ))}
            </select>
          </div>

          {selectedUser && (
            <div className="bg-gray-800/50 rounded-lg p-3">
              <p className="text-white font-semibold">{selectedUserInfo?.name || selectedUserInfo?.email}</p>
              <p className="text-gray-400 text-sm">Current mode: <span className={`font-bold ${
                tradeSettings?.trade_mode === 'always_win' ? 'text-green-400' :
                tradeSettings?.trade_mode === 'always_lose' ? 'text-red-400' :
                tradeSettings?.trade_mode === 'custom' ? 'text-yellow-400' : 'text-gray-300'
              }`}>{tradeSettings?.trade_mode || 'normal'}</span>
              {tradeSettings?.trade_mode === 'custom' && <span className="text-yellow-400"> ({tradeSettings?.win_rate}% win)</span>}
              </p>
            </div>
          )}
        </div>

        {selectedUser && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <div>
              <label className="text-gray-400 text-xs mb-1 block">Trade Mode</label>
              <select
                value={settingsForm.trade_mode}
                onChange={(e) => setSettingsForm({ ...settingsForm, trade_mode: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                data-testid="trade-mode-select"
              >
                <option value="normal">Normal (Market)</option>
                <option value="always_win">Always Win</option>
                <option value="always_lose">Always Lose</option>
                <option value="custom">Custom Win Rate</option>
              </select>
            </div>

            {settingsForm.trade_mode === 'custom' && (
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Win Rate (%)</label>
                <input
                  type="number" min="0" max="100"
                  value={settingsForm.win_rate}
                  onChange={(e) => setSettingsForm({ ...settingsForm, win_rate: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  data-testid="win-rate-input"
                />
              </div>
            )}

            <div>
              <label className="text-gray-400 text-xs mb-1 block">Apply To</label>
              <select
                value={settingsForm.apply_to}
                onChange={(e) => setSettingsForm({ ...settingsForm, apply_to: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                data-testid="apply-to-select"
              >
                <option value="all">All Trades</option>
                <option value="binary">Binary Only</option>
                <option value="arbitrage">Arbitrage Only</option>
              </select>
            </div>

            <div>
              <label className="text-gray-400 text-xs mb-1 block">Reason</label>
              <input
                type="text"
                value={settingsForm.reason}
                onChange={(e) => setSettingsForm({ ...settingsForm, reason: e.target.value })}
                placeholder="Optional note"
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              />
            </div>
          </div>
        )}

        {selectedUser && (
          <button
            onClick={saveUserTradeSettings}
            disabled={settingsLoading}
            className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold disabled:opacity-50"
            data-testid="save-trade-settings-btn"
          >
            {settingsLoading ? 'Saving...' : 'Save Trade Settings'}
          </button>
        )}
      </div>

      {/* Binary Trades */}
      <div data-testid="binary-trades-section">
        <h2 className="text-lg font-bold text-white mb-4 flex items-center">
          <TrendingUp className="h-5 w-5 mr-2 text-green-400" />
          Binary Trades Control
          <span className="ml-3 px-3 py-1 rounded-full text-xs font-semibold bg-green-500/20 text-green-400">
            {binaryTrades.length} Active
          </span>
        </h2>

        {binaryTrades.length === 0 ? (
          <div className="bg-gray-900 rounded-xl p-8 text-center border border-gray-800">
            <AlertCircle className="h-10 w-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No active binary trades</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {binaryTrades.map((trade) => {
              const id = trade.trade_id || trade.id;
              return (
                <div key={id} className="bg-gray-900 rounded-xl p-5 border border-gray-800 hover:border-purple-500/50 transition-colors" data-testid={`binary-trade-${id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center space-x-4">
                      <div className={`p-3 rounded-lg ${trade.prediction === 'up' ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                        {trade.prediction === 'up' ? <TrendingUp className="h-6 w-6 text-green-400" /> : <TrendingDown className="h-6 w-6 text-red-400" />}
                      </div>
                      <div>
                        <p className="text-white font-bold">{formatPair(trade.crypto_symbol || 'BTC')}</p>
                        <p className="text-gray-400 text-sm">
                          {(trade.prediction || '').toUpperCase()} &bull; ${fmtNumber(trade.amount, 2)} &bull; {trade.duration_seconds}s
                        </p>
                        <p className="text-gray-500 text-xs">User: {trade.user_id?.substring(0, 12)}...</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleBinaryOutcome(id, 'win')}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white font-semibold text-sm"
                        data-testid={`set-win-${id}`}
                      >
                        SET WIN
                      </button>
                      <button
                        onClick={() => handleBinaryOutcome(id, 'lose')}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white font-semibold text-sm"
                        data-testid={`set-lose-${id}`}
                      >
                        SET LOSE
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* AI Arbitrage */}
      <div data-testid="arbitrage-section">
        <h2 className="text-lg font-bold text-white mb-4 flex items-center">
          <Zap className="h-5 w-5 mr-2 text-yellow-400" />
          AI Arbitrage Control
          <span className="ml-3 px-3 py-1 rounded-full text-xs font-semibold bg-yellow-500/20 text-yellow-400">
            {arbitrageInvestments.length} Active
          </span>
        </h2>

        {arbitrageInvestments.length === 0 ? (
          <div className="bg-gray-900 rounded-xl p-8 text-center border border-gray-800">
            <AlertCircle className="h-10 w-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No active arbitrage investments</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {arbitrageInvestments.map((inv) => {
              const id = inv.investment_id || inv.id;
              return (
                <div key={id} className="bg-gray-900 rounded-xl p-5 border border-gray-800 hover:border-purple-500/50 transition-colors" data-testid={`arb-${id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 rounded-lg bg-yellow-500/20">
                        <Zap className="h-6 w-6 text-yellow-400" />
                      </div>
                      <div>
                        <p className="text-white font-bold">{(inv.package_name || 'Standard').toUpperCase()}</p>
                        <p className="text-gray-400 text-sm">
                          ${fmtNumber(inv.amount, 2)} &bull; {inv.period_days || Math.round((inv.period_hours || 0) / 24)}d &bull; Expected: +${fmtNumber(inv.expected_return || inv.expected_profit || 0, 2)}
                        </p>
                        <p className="text-gray-500 text-xs">User: {inv.user_id?.substring(0, 12)}...</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleArbitrageOutcome(id, 'win_full')}
                        className="px-3 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm font-semibold"
                      >
                        WIN (100%)
                      </button>
                      <button
                        onClick={() => handleArbitrageOutcome(id, 'lose_partial')}
                        className="px-3 py-2 bg-yellow-600 hover:bg-yellow-700 rounded-lg text-white text-sm font-semibold"
                      >
                        LOSE (50%)
                      </button>
                      <button
                        onClick={() => handleArbitrageOutcome(id, 'lose_full')}
                        className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm font-semibold"
                      >
                        LOSE (Full)
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TradingControl;
